import * as React from "react";
import { useIntl, defineMessages } from "react-intl";

/**
 * Figma Node IDs:
 * Design Frame: 339:22623
 * Instance IDs: I330:8483;330:8469, I330:8483;2456:5671, I330:8483;330:8476, I330:8483;330:8478, I330:8484;330:8469, I330:8484;2456:5671, I330:8484;330:8476, I330:8484;330:8478, I330:8485;330:8469, I330:8485;2456:5671, I330:8485;330:8476, I330:8485;330:8478, I339:22295;330:8469, I339:22295;2456:5671, I339:22295;330:8473, I339:22295;330:8476, I339:22295;330:8478
 * Composite: true
 * Last Updated: 2025-01-13
 * Generated by: Claude Code figma-component command
 */

import { RichText } from "@learningblocks/shared-components/lib/RichText";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { fetchTranslations } from "./locales/config";

import { TextArea } from "../TextArea";
import { LxButton } from "../LxButton";

import * as styles from "./OpenQuestion.css";

const messages = defineMessages({
  submitAnswer: {
    id: "openQuestion.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Button text to submit the learner's answer",
  },
  resetQuestion: {
    id: "openQuestion.resetQuestion",
    defaultMessage: "Reset question",
    description: "Button text to reset the question and start over",
  },
  answerLabel: {
    id: "openQuestion.answerLabel",
    defaultMessage: "ANSWER",
    description: "Label shown above the sample answer",
  },
  enterYourAnswer: {
    id: "openQuestion.enterYourAnswer",
    defaultMessage: "Enter your answer",
    description: "Placeholder text for the textarea input",
  },
});

export interface OpenQuestionProps {
  // Required props (drive core behavior)
  question: string;

  // Optional props without defaults (pure customization)
  answer?: string;
  value?: string;
  onSubmit?: (answer: string) => void;
  onReset?: () => void;
  onChange?: (value: string) => void;
  className?: string;
  testId?: string;

  // Optional props with defaults (come last)
  placeholder?: string;
  showAnswer?: boolean;
  disabled?: boolean;
  maxLength?: number;
  enableMath?: boolean;
}

const OpenQuestionInternal = ({
  question,
  answer,
  value: controlledValue,
  onSubmit,
  onReset,
  onChange,
  className,
  testId,
  placeholder,
  showAnswer = false,
  disabled = false,
  maxLength,
  enableMath = false,
}: OpenQuestionProps) => {
  const intl = useIntl();
  const [internalValue, setInternalValue] = React.useState("");
  const [isAnswered, setIsAnswered] = React.useState(showAnswer);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = React.useCallback(
    (newValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [controlledValue, onChange]
  );

  const handleSubmit = React.useCallback(() => {
    if (value.trim()) {
      setIsAnswered(true);
      onSubmit?.(value);
    }
  }, [value, onSubmit]);

  const handleReset = React.useCallback(() => {
    setIsAnswered(false);
    if (controlledValue === undefined) {
      setInternalValue("");
    }
    onReset?.();
  }, [controlledValue, onReset]);

  const isSubmitDisabled = disabled || !value.trim();

  return (
    <div
      className={`${styles.openQuestionContainer} ${className || ""}`}
      data-testid={testId}
    >
      {/* Question */}
      <div className={styles.questionSection}>
        <RichText value={question} equation={enableMath} />
      </div>

      {/* Text Area */}
      <TextArea
        value={value}
        onChange={handleChange}
        placeholder={
          placeholder || intl.formatMessage(messages.enterYourAnswer)
        }
        disabled={disabled || isAnswered}
        maxLength={maxLength}
        className={styles.textAreaWrapper}
        testId={testId ? `${testId}-textarea` : undefined}
      />

      {/* Answer Display (shown after submission) */}
      {isAnswered && answer && (
        <div className={styles.answerSection}>
          <div className={styles.answerLabel}>
            {intl.formatMessage(messages.answerLabel)}
          </div>
          <div className={styles.answerText}>
            <RichText value={answer} equation={enableMath} />
          </div>
        </div>
      )}

      {/* Button Container */}
      <div className={styles.buttonContainer}>
        {!isAnswered ? (
          <LxButton
            variant="primary"
            text={messages.submitAnswer.id}
            onClickHandler={handleSubmit}
            disabled={isSubmitDisabled}
            testId={testId ? `${testId}-submit` : undefined}
          />
        ) : (
          <LxButton
            variant="secondary"
            text={messages.resetQuestion.id}
            onClickHandler={handleReset}
            disabled={disabled}
            testId={testId ? `${testId}-reset` : undefined}
          />
        )}
      </div>
    </div>
  );
};

export const OpenQuestion = (props: OpenQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <OpenQuestionInternal {...props} />
  </TranslationsProvider>
);
